/**
 * 本地数据库（IndexedDB + Dexie）。
 *
 * 阶段 2：把阶段 1 的静态数据落库，并把训练会话/每组记录/饮食/体重/每日意图持久化，
 * 实现「刷新恢复未完成训练」「每组即时落库」「联网补同步队列」。
 *
 * 约定（§25 / 决策 14.x）：
 *   - 主键一律使用客户端生成的 UUID（服务端同值），用于幂等与跨设备合并。
 *   - 事件同步：每次重要写操作往 `syncEvents` 写入一次（阶段 4 接入服务端 push/pull）。
 *   - LocalStorage 只用于非关键 UI 设置；关键数据一律在 IndexedDB（见 useAppStore 注释）。
 *   - 版本化：db.version(n) 驱动迁移；schema 变更同步递增 SCHEMA_VERSION 与 seedVersion。
 */
import Dexie, { type EntityTable } from "dexie";
import type {
  AiTaskStatus,
  BodyMeasurement,
  DailyIntent,
  Exercise,
  MealEntry,
  ProgressionDecision,
  SyncEvent,
  TrainingTemplate,
  WorkoutExercise,
  WorkoutSession,
  WorkoutSet,
} from "@jilian/shared";

export interface AppSetting {
  key: string;
  value: unknown;
  updatedAt: string;
}

export interface AiTask {
  id: string;
  status: AiTaskStatus;
  kind: string | null;
  payloadJson: string | null;
  retryCount: number;
  createdAt: string;
  updatedAt: string;
}

export const DB_NAME = "jilian";

export const db = new Dexie(DB_NAME) as Dexie & {
  meta: EntityTable<AppSetting, "key">;
  exercises: EntityTable<Exercise, "id">;
  sessions: EntityTable<WorkoutSession, "id">;
  exerciseItems: EntityTable<WorkoutExercise, "id">;
  sets: EntityTable<WorkoutSet, "id">;
  meals: EntityTable<MealEntry, "id">;
  measurements: EntityTable<BodyMeasurement, "id">;
  dailyIntent: EntityTable<DailyIntent, "id">;
  syncEvents: EntityTable<SyncEvent, "id">;
  aiTasks: EntityTable<AiTask, "id">;
  /** 阶段 4：同步通用实体仓储（权威值 + extras 双版本，key = `type:id` 或 `type:id#vN`） */
  syncEntities: EntityTable<SyncEntityRecord, "key">;
  /** 阶段 7：进阶决策落库（导出 CSV 数据源；服务端同步同实体） */
  progressionDecisions: EntityTable<ProgressionDecision, "id">;
  /** 训练序列模板（自定义训练计划） */
  trainingTemplates: EntityTable<TrainingTemplate, "id">;
};

/** 同步通用实体记录（与服务端 sync_entities 对应；typed 表是读投影，双版本在此留档）。 */
export interface SyncEntityRecord {
  key: string; // `${entityType}:${entityId}` 或 `${key}#v${version}`（双版本保留）
  entityType: string;
  entityId: string;
  version: number;
  payload: unknown;
  updatedAt: string;
}

// 本地结构版本：任何影响既有数据的 schema/种子变更都要递增，并配迁移。
db.version(1).stores({
  meta: "key",
  exercises: "id, nameZh, nameEn, lockedUntilMedicalClearance, primaryMuscles",
  sessions: "id, userId, status, localDateAtStart, startedAt, updatedAt",
  exerciseItems: "id, sessionId, exerciseId, orderIndex",
  sets: "id, workoutExerciseId, setIndex, completedAt, updatedAt",
  meals: "id, userId, consumedAt, mealType, processingStatus, updatedAt",
  measurements: "id, userId, date, updatedAt",
  dailyIntent: "id, &[userId+localDate], userId, localDate, updatedAt",
  syncEvents: "id, entityType, entityId, syncStatus, clientCreatedAt",
  aiTasks: "id, status, createdAt, updatedAt",
});

// 阶段 4：新增通用同步实体表（非破坏性迁移；既有数据原样保留）
db.version(2).stores({
  syncEntities: "key, entityType, entityId, version, updatedAt",
});

// 阶段 7：新增进阶决策表（导出数据源；非破坏性迁移）
db.version(3).stores({
  progressionDecisions: "id, userId, exerciseId, sourceSessionId, createdAt",
});

// 训练序列模板（自定义训练计划）
db.version(4).stores({
  trainingTemplates: "id, userId, position, isOptionalLightDay, source, updatedAt",
});

// 阶段 8：sessions 增加 planType 索引（非破坏性迁移）
db.version(5).stores({
  sessions: "id, userId, status, planType, localDateAtStart, startedAt, updatedAt",
});

if (import.meta.env.DEV) {
  // 开发期方便排查：暴露到 window（勿在生产依赖）。
  (window as unknown as { jilianDb?: typeof db }).jilianDb = db;
}
